// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadProductCategories from "@conklin/App/Handlers/XNG/ProductCategories";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import FeaturedCategoryCard from "@conklin/App/Widgets/FeaturedCategories/FeaturedCategoryCard";
import { XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { CategoryModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
    linkTitle = "linkTitle",
    linkUrl = "linkUrl",
    parentCategories = "parentCategories",
}

interface FeaturedCategoryFields {
    fields: {
        [fields.title]: string;
        [fields.linkTitle]: string;
        [fields.linkUrl]: LinkFieldValue;
        [fields.parentCategories]: string[];
    };
}

const enum fields {
    mainTitle = "mainTitle",
    cards = "cards",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.mainTitle]: string;
        [fields.cards]: FeaturedCategoryFields[];
    };
}

export interface FeaturedCategoriesStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    mainTitle?: TypographyProps;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const parentCategoriesMaped = props.fields.cards.reduce((acc: string[], item) => {
        return acc.concat(item.fields.parentCategories);
    }, []);

    const viewKey = getDataViewKey({ categories: parentCategoriesMaped });
    const categoriesState = (state as CustomAppState).xProductCategories.views?.[viewKey];
    return {
        parentCategories: categoriesState?.categories,
        isLoading: categoriesState?.loading,
        parentCategoriesMaped,
    };
};

const mapDispatchToProps = {
    loadProductCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {};

const styles = featuredCategoriesStyles;
const FeaturedCategories: FC<Props> = ({
    fields,
    parentCategories,
    isLoading,
    loadProductCategories,
    parentCategoriesMaped,
}) => {
    useEffect(() => {
        if (!isLoading && !parentCategories && fields.cards.length > 0) {
            loadProductCategories({
                categories: parentCategoriesMaped,
            });
        }
    }, [fields.cards]);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="product-categories-widget">
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner size={24} />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    if (!parentCategories && !isLoading) {
        return null;
    }

    // Helper function to get category details by ID
    const getCategoryDetailsByIds = (ids: string[], categories: CategoryModel[]) => {
        return ids.map(id => {
            const category = categories.find(cat => cat.id === id);
            if (category) {
                return {
                    name: category.name,
                    shortDescription: category.shortDescription,
                    imageSrc: category.smallImagePath || category.largeImagePath,
                    path: category.path,
                    id: category.id,
                };
            }
            return null;
        });
    };

    return (
        <>
            {fields.mainTitle && <Typography {...styles.mainTitle}>{fields.mainTitle}</Typography>}
            <StyledWrapper {...styles.wrapper} data-test-selector="featured-categories-widget">
                {fields.cards.map((card, index) => {
                    const categories = getCategoryDetailsByIds(card.fields.parentCategories, parentCategories);
                    return (
                        <StyledWrapper key={index}>
                            <FeaturedCategoryCard
                                title={card.fields.title}
                                linkTitle={card.fields.linkTitle}
                                linkUrl={card.fields.linkUrl}
                                categories={categories}
                            />
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedCategories),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.mainTitle,
                editorTemplate: "TextField",
                displayName: "Main Title",
                defaultValue: "Featured Products",
            },
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title || "Category Card";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: fields.title,
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "Title",
                    },
                    {
                        name: fields.linkTitle,
                        displayName: "Link Title",
                        editorTemplate: "TextField",
                        defaultValue: "View Products",
                        isRequired: false,
                    },
                    {
                        name: fields.linkUrl,
                        displayName: "Link URL",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: fields.parentCategories,
                        editorTemplate: "CategoriesField",
                        displayName: "Heading Category",
                        defaultValue: [],

                        validate: (value, item) => {
                            if (value.length > 50) {
                                return "50 Categories are allowed to select!";
                            }
                            return "";
                        },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
